/**
 * GridSection Component
 *
 * Dynamic grid section (max 4x4) with per-cell collections of primitives.
 * Uses Bootstrap grid utilities (row-cols-*, g-*) and border/rounded classes.
 */
import React from "react";
import { filterClass } from "../../utils/class-utils";
import { getHorizontalContentAlign, getSectionBlockAlignClass } from "../utils/content-alignment";
import type { GridSectionProps } from "../types";

const clampInt = (value: string | undefined, min: number, max: number, fallback: number) => {
  const n = parseInt(value || "", 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

/** Responsive row-cols classes: stacks to 1 col on md and below. */
function responsiveRowColsClasses(numColumns: number): string[] {
  if (numColumns <= 2) return ["row-cols-1", "row-cols-md-2"];
  if (numColumns === 3) return ["row-cols-1", "row-cols-md-2", "row-cols-xl-3"];
  return ["row-cols-1", "row-cols-md-2", "row-cols-xl-4"];
}

export function GridSection(props: GridSectionProps) {
  const {
    rows = "2",
    columns = "2",

    paddingYClass = "py-section-md",
    bgClass = "no-class",
    verticalAlignClass = "justify-content-start",
    contentAlignClass = "align-items-start text-start",

    Cell1,
    Cell2,
    Cell3,
    Cell4,
    Cell5,
    Cell6,
    Cell7,
    Cell8,
    Cell9,
    Cell10,
    Cell11,
    Cell12,
    Cell13,
    Cell14,
    Cell15,
    Cell16,
  } = props;

  const numColumns = clampInt(columns, 1, 4, 2);
  const numRows = clampInt(rows, 1, 4, 2);
  const maxCells = numColumns * numRows;
  const inheritedContentAlign = getHorizontalContentAlign(contentAlignClass) ?? "start";

  const cells: Array<GridSectionProps["Cell1"]> = [
    Cell1, Cell2, Cell3, Cell4,
    Cell5, Cell6, Cell7, Cell8,
    Cell9, Cell10, Cell11, Cell12,
    Cell13, Cell14, Cell15, Cell16,
  ].slice(0, 16);

  const sectionClasses = [
    bgClass,
    filterClass(paddingYClass),
  ]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  const containerClasses = [
  ]
    .filter(Boolean)
    .join(" ");

  /**
   * ===== CLASSES (frontend can edit here) =====
   */
  const classes = {
    section: ["section section-grid", sectionClasses].filter(Boolean).join(" ").trim(),
    container: ["container-xl", containerClasses].filter(Boolean).join(" ").trim(),
    row: ["row", ...responsiveRowColsClasses(numColumns), "row-gap-5"].join(" "),
    col: ["col d-flex flex-column gap-4", filterClass(verticalAlignClass), getSectionBlockAlignClass(contentAlignClass)].filter(Boolean).join(" "),
    cellItem: "w-100 component-item",
  };

  return (
    <section className={classes.section || undefined}>
      <div className={classes.container || undefined}>
        <div className={classes.row}>
          {cells.slice(0, maxCells).map((cellItems, cellIdx) => (
            <div key={cellIdx} className={classes.col}>
              {cellItems?.map((item, itemIdx) => {
                const ItemType = item.type as React.ComponentType<any>;
                // eslint-disable-next-line react/no-array-index-key
                return (
                  <div key={itemIdx} className={classes.cellItem}>
                    <ItemType {...item.props} inheritedContentAlign={inheritedContentAlign} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default GridSection;


